import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode, Ref } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import AccommodationRoomDetailsList from '../AccommodationRoomDetailsList'
import Clickable from 'components/Common/Clickable'
import { accommodationSameRoomTileGridChildEnforcer } from './AccommodationSameRoomTileGridBase'
import TextLink from 'LuxKit/TextLink'
import { bedbankPlainDescription, getHotelRoomTypeName, mapRoomPackage } from '../roomPackageUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

export const StyledAccommodationSameRoomTileDetails = styled(Group)`
  grid-area: details;
  ${accommodationSameRoomTileGridChildEnforcer}
  padding-block: ${rem(20)} ${rem(12)};
  padding-inline: ${rem(20)};
  min-width: 0;

  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    padding-bottom: ${rem(20)};
  }
`

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage | App.Property.Room
  urgencyLabels?: ReactNode
  hideCounts?: boolean
  actions?: ReactNode
  roomCount: number
  onShowRoomDetails?: React.MouseEventHandler<HTMLButtonElement>
  showHotelRoomAmenities?: boolean
  containerRef?: Ref<HTMLDivElement>
}

function AccommodationSameRoomTileDetails(props: PropsWithChildren<Props>) {
  const {
    actions,
    children,
    roomPackage,
    urgencyLabels,
    hideCounts,
    roomCount,
    onShowRoomDetails,
    showHotelRoomAmenities = false,
    containerRef,
  } = props

  const intl = useIntl()

  const name = mapRoomPackage(
    roomPackage,
    propertyRoom => propertyRoom.name,
    hotelPkg => getHotelRoomTypeName(hotelPkg.roomType),
    bedbankPkg => bedbankPkg.name,
  )

  const description = mapRoomPackage(
    roomPackage,
    propertyRoom => propertyRoom.description,
    hotelPkg => hotelPkg.roomType.description,
    bedbankPkg => bedbankPlainDescription(bedbankPkg.description),
  )

  const viewRoomDetailsText = intl.formatMessage({
    id: 'W-7laTMCDOxc3XNUuOGH',
    defaultMessage: 'View room details',
    description: 'Link text to open the room details modal. dialog window',
  })

  const headingText = roomCount > 1
    ? intl.formatMessage(
        {
          id: 'sKm8lePKALAPgd0iMYGT',
          defaultMessage: '{count}x {roomName}',
          description: 'Heading showing room count multiplier and room name (e.g. "2x Deluxe Suite").',
        },
        { count: roomCount, roomName: name },
      )
    : name

  return <StyledAccommodationSameRoomTileDetails ref={containerRef} direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Heading variant="heading4">{headingText}</Heading>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
    </VerticalSpacer>
    {description && !showHotelRoomAmenities && <CSSBreakpoint min="tablet"><BodyText variant="medium">{description}</BodyText></CSSBreakpoint>}
    {onShowRoomDetails && !showHotelRoomAmenities && <>
      <CSSBreakpoint max="tablet">
        <TextLink onClick={onShowRoomDetails} size="medium">{viewRoomDetailsText}</TextLink>
      </CSSBreakpoint>
      <Clickable onClick={onShowRoomDetails}>
        <AccommodationRoomDetailsList roomPackage={roomPackage} urgencyLabels={urgencyLabels} hideCounts={hideCounts} />
      </Clickable>
    </>}
    {showHotelRoomAmenities && <AccommodationRoomDetailsList
      roomPackage={roomPackage}
      urgencyLabels={urgencyLabels}
      hideCounts={hideCounts}
      showHotelRoomAmenities
      showAmenitiesAndPoliciesCtaOnNewLine
    />}
    {!onShowRoomDetails && !showHotelRoomAmenities && <AccommodationRoomDetailsList roomPackage={roomPackage} urgencyLabels={urgencyLabels} hideCounts={hideCounts} />}
    {children}
  </StyledAccommodationSameRoomTileDetails>
}

export default AccommodationSameRoomTileDetails
